import {
  type AlertVariant,
  SimpleAlert,
} from "@/components/shared/alerts/simple-alert";
import { TagAlert } from "@/components/shared/alerts/tag-alert";
import { DuplicatesAlert } from "@/features/invoice-standard-review/components/duplicates-alert";
import type { TranslationKey } from "@/localisation/types";
import { useTenant } from "@/providers/session";
import type { DuplicateInvoice } from "@/queries/invoices/get";
import { INVOICE_QUERY } from "@/queries/invoices/get";
import { useUpdateInvoiceTagApproval } from "@/queries/invoices/update-tag-approval";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// alerts are still translated inside this component; tags come precomputed

type PreparedTag = {
  tagId: string;
  tagName: string;
  isApproved: boolean;
  title: string;
  message?: string;
  subMessage?: string;
  details: Record<string, { flag: boolean; reasoning: string | null }>;
};

type Props = {
  tags: PreparedTag[] | null;
  alerts: AlertItem[];
  invoiceId?: string;
  duplicates?: DuplicateInvoice[];
  isDuplicate?: boolean;
  translationKey: string;
};

type AlertItem = {
  tagType: "multipleInvoice" | "missingVatId";
  tagDisplayVariant: AlertVariant;
};

export const InvoiceAlert = ({
  tags,
  alerts,
  invoiceId,
  duplicates,
  isDuplicate,
  translationKey,
}: Props) => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const updateInvoiceTagApproval = useUpdateInvoiceTagApproval();
  const [votedTags, setVotedTags] = useState<Set<string>>(new Set());

  const handleVote = async (tagId: string, approval: boolean) => {
    if (!invoiceId) return;

    try {
      await updateInvoiceTagApproval({
        variables: {
          tagId,
          invoiceId,
          tenantId,
          approval,
        },
        refetchQueries: [
          {
            query: INVOICE_QUERY,
            variables: { invoiceId },
          },
        ],
        awaitRefetchQueries: true,
      });
      toast.success(t("invoiceReview.feedback.feedback_submitted"));

      setVotedTags((prev) => new Set(Array.from(prev).concat(tagId)));
    } catch (error) {
      console.error("Error submitting tag feedback:", error);
    }
  };

  if (alerts.length === 0 && (!tags || tags.length === 0) && !isDuplicate) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2 mb-2">
      {tags?.map((tag) => (
        <TagAlert
          key={tag.tagId}
          tagId={tag.tagId}
          tagName={tag.tagName}
          isApproved={tag.isApproved}
          hasVoted={votedTags.has(tag.tagId)}
          onVote={handleVote}
          title={tag.title}
          message={tag.message}
          subMessage={tag.subMessage}
          details={tag.details}
        />
      ))}

      {isDuplicate && duplicates && <DuplicatesAlert invoices={duplicates} />}
      {alerts.map((alert) => {
        return (
          <SimpleAlert
            key={alert.tagType}
            title={
              t(
                `${translationKey}.${alert.tagType}.title` as TranslationKey,
              ) as string
            }
            message={
              t(
                `${translationKey}.${alert.tagType}.message` as TranslationKey,
              ) as string
            }
            variant={alert.tagDisplayVariant}
          />
        );
      })}
    </div>
  );
};
